import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { BookOpen, Activity, AlertCircle, CheckCircle2, ExternalLink } from 'lucide-react';
import PageHeader from '@/components/PageHeader';

const Methodology = ({ t }) => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0f1419] p-4 md:p-8 pb-48">
      <div className="max-w-4xl mx-auto">
        <PageHeader t={t} />
        
        <div className="text-center mb-8">
          <BookOpen className="w-16 h-16 mx-auto mb-4 text-purple-400" />
          <h1 className="text-4xl font-bold text-white mb-2">
            {t?.methodologyTitle || "Méthodologie Scientifique HEAREN"}
          </h1>
          <p className="text-xl text-white/60">
            {t?.methodologySubtitle || "Fondements et limitations de notre approche"}
          </p>
        </div>

        {/* Standards de référence */}
        <Card className="mb-6 border-2 border-blue-500/50 bg-[#1a1f2e] rounded-2xl shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-400">
              <CheckCircle2 className="h-6 w-6 text-blue-400" />
              {t?.referenceStandards || "Standards de référence"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="bg-blue-500/10 p-4 rounded-lg border border-blue-500/20">
                <h3 className="font-semibold text-blue-300 mb-2">ISO 389-8:2004</h3>
                <p className="text-sm text-white/80">
                  {t?.iso389desc || "Acoustique - Zéro de référence pour l'étalonnage d'équipements audiométriques - Partie 8: Niveaux de référence équivalents de seuil pour les écouteurs purs à conduction aérienne dans le coupleur acoustique HA-1."}
                </p>
                <p className="text-xs text-white/60 mt-2">
                  <strong className="text-blue-300">{t?.application || "Application"}</strong>: {t?.iso389app || "Conversion dB HL → dB SPL pour chaque fréquence de test."}
                </p>
              </div>

              <div className="bg-blue-500/10 p-4 rounded-lg border border-blue-500/20">
                <h3 className="font-semibold text-blue-300 mb-2">ISO 8253-1:2010</h3>
                <p className="text-sm text-white/80">
                  {t?.iso8253desc || "Acoustique - Méthodes d'essais audiométriques - Partie 1: Audiométrie tonale liminaire par conduction aérienne et par conduction osseuse."}
                </p>
                <p className="text-xs text-white/60 mt-2">
                  <strong className="text-blue-300">{t?.application || "Application"}</strong>: {t?.iso8253app || "Méthode par paliers ascendants pour la détection des seuils auditifs."}
                </p>
              </div>

              <div className="bg-blue-500/10 p-4 rounded-lg border border-blue-500/20">
                <h3 className="font-semibold text-blue-300 mb-2">ISO 226:2003</h3>
                <p className="text-sm text-white/80">
                  {t?.iso226desc || "Acoustique - Lignes isosoniques normales (courbes d'égale sonie)."}
                </p>
                <p className="text-xs text-white/60 mt-2">
                  <strong className="text-blue-300">{t?.application || "Application"}</strong>: {t?.iso226app || "Prise en compte de la perception fréquence-dépendante de l'oreille humaine."}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Méthode HEAREN */}
        <Card className="mb-6 border-2 border-green-500/50 bg-[#1a1f2e] rounded-2xl shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-400">
              <Activity className="h-6 w-6 text-green-400" />
              {t?.hearenMethod || "Méthode HEAREN"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-green-300 mb-2">
                  {t?.calibrationPhase || "1. Phase de calibration"}
                </h3>
                <ul className="text-sm text-white/80 space-y-2 ml-4">
                  <li><strong className="text-green-300">{t?.spatialTest || "Test de spatialisation"}</strong>: {t?.spatialTestDesc || "Vérification du fonctionnement correct du casque (gauche/droite)."}</li>
                  <li><strong className="text-green-300">{t?.multiFreqCalibration || "Calibration multi-fréquences"}</strong>: {t?.multiFreqCalibrationDesc || "Détermination des seuils de référence sur 3 fréquences (500 Hz, 1000 Hz, 4000 Hz) couvrant le spectre grave-aigu."}</li>
                  <li><strong className="text-green-300">{t?.ascendingMethod || "Méthode par paliers ascendants"}</strong>: {t?.ascendingMethodDesc || "Ton pur commençant à 0 dB HL, augmentation de 5 dB toutes les 2 secondes jusqu'à perception. 3 confirmations par fréquence pour fiabilité."}</li>
                  <li><strong className="text-green-300">{t?.calibrationOffset || "Calcul de l'offset de calibration"}</strong>: {t?.calibrationOffsetDesc || "Moyenne pondérée des écarts entre seuils mesurés et attendus, avec poids accru pour 1000 Hz (référence audiométrique standard)."}</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-green-300 mb-2">
                  {t?.testPhase || "2. Phase de test audiométrique"}
                </h3>
                <ul className="text-sm text-white/80 space-y-2 ml-4">
                  <li><strong className="text-green-300">{t?.frequencies || "Fréquences testées"}</strong>: 125, 250, 500, 1000, 2000, 4000, 8000 Hz</li>
                  <li><strong className="text-green-300">{t?.volumeCalculation || "Calcul du volume"}</strong>: {t?.volumeCalculationDesc || "Conversion dB HL → dB SPL via tables ISO 389-8, puis dB SPL → amplitude digitale, avec application de l'offset de calibration."}</li>
                  <li><strong className="text-green-300">{t?.autoIncrement || "Incrémentation automatique"}</strong>: {t?.autoIncrementDesc || "Paliers de 5 dB HL toutes les 3 secondes, plage 0-120 dB HL."}</li>
                  <li><strong className="text-green-300">{t?.testPerEar || "Test par oreille"}</strong>: {t?.testPerEarDesc || "Chaque fréquence testée sur oreille gauche puis droite."}</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-green-300 mb-2">
                  {t?.reliabilityScore || "3. Score de fiabilité"}
                </h3>
                <ul className="text-sm text-white/80 space-y-2 ml-4">
                  <li><strong className="text-green-300">{t?.symmetry || "Symétrie gauche-droite"}</strong>: {t?.symmetryDesc || "Pénalité si écarts supérieurs à 10 dB entre oreilles (pathologie ou erreur de mesure)."}</li>
                  <li><strong className="text-green-300">{t?.slope || "Pente audiométrique"}</strong>: {t?.slopeDesc || "Détection de sauts supérieurs à 30 dB entre fréquences adjacentes (incohérent physiologiquement)."}</li>
                  <li><strong className="text-green-300">{t?.extremeValues || "Valeurs extrêmes"}</strong>: {t?.extremeValuesDesc || "Alerte si seuils inférieurs à -10 dB HL (suspect) ou supérieurs à 90 dB HL (bruit ambiant ou problème matériel)."}</li>
                  <li><strong className="text-green-300">{t?.finalScore || "Score final"}</strong>: 100% à 0%, avec seuils qualité: Excellent (≥90%), Bon (≥75%), Acceptable (≥60%), Faible (moins de 60%).</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Adaptations pour environnement web */}
        <Card className="mb-6 border-2 border-yellow-500/50 bg-[#1a1f2e] rounded-2xl shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-yellow-400">
              <AlertCircle className="h-6 w-6 text-yellow-400" />
              {t?.webAdaptations || "Adaptations pour environnement web"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Alert className="bg-yellow-500/10 border-yellow-500/30">
              <AlertDescription className="text-yellow-100 text-sm">
                {t?.webAdaptationsDesc || "Contrairement à un audiomètre clinique étalonné, HEAREN fonctionne dans un environnement non-contrôlé. Voici nos adaptations :"}
              </AlertDescription>
            </Alert>

            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-2">
                <span className="text-yellow-400 font-bold">•</span>
                <div className="text-white/90">
                  <strong className="text-yellow-300">{t?.relativeCalibration || "Calibration relative"}</strong>: <span className="text-white/80">{t?.relativeCalibrationDesc || "Au lieu d'une calibration absolue (impossible sans matériel certifié), nous utilisons une calibration relative basée sur les seuils de l'utilisateur."}</span>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-yellow-400 font-bold">•</span>
                <div className="text-white/90">
                  <strong className="text-yellow-300">{t?.multiFreqValidation || "Validation multi-fréquences"}</strong>: <span className="text-white/80">{t?.multiFreqValidationDesc || "3 points de calibration (vs 1 seul en général) pour détecter les incohérences matérielles."}</span>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-yellow-400 font-bold">•</span>
                <div className="text-white/90">
                  <strong className="text-yellow-300">{t?.reliabilityScoring || "Scoring de fiabilité"}</strong>: <span className="text-white/80">{t?.reliabilityScoringDesc || "Transparence sur la qualité des mesures, permettant à l'utilisateur d'identifier les tests à refaire."}</span>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-yellow-400 font-bold">•</span>
                <div className="text-white/90">
                  <strong className="text-yellow-300">{t?.safetyLimits || "Limites de sécurité"}</strong>: <span className="text-white/80">{t?.safetyLimitsDesc || "Volume maximal limité à 0.9 (90% amplitude) pour prévenir tout risque auditif, même en cas d'erreur."}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Usage approprié */}
        <Card className="mb-6 border-2 border-purple-500/50 bg-[#1a1f2e] rounded-2xl shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-purple-400">
              <CheckCircle2 className="h-6 w-6 text-purple-400" />
              {t?.appropriateUse || "Usage approprié de HEAREN"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-3 text-sm text-white/80">
              <div className="flex items-start gap-2">
                <span className="text-green-400 font-bold text-lg">✓</span>
                <div>
                  <strong className="text-purple-300">{t?.use1 || "Évaluation préliminaire"}</strong>: {t?.use1Desc || "Obtenir une première indication de son profil auditif avant consultation ORL."}
                </div>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-green-400 font-bold text-lg">✓</span>
                <div>
                  <strong className="text-purple-300">{t?.use2 || "Personnalisation sonore"}</strong>: {t?.use2Desc || "Adapter les paramètres sonores selon le profil."}
                </div>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-green-400 font-bold text-lg">✓</span>
                <div>
                  <strong className="text-purple-300">{t?.use3 || "Suivi indicatif"}</strong>: {t?.use3Desc || "Comparer l'évolution dans le temps (avec les mêmes conditions: casque, environnement)."}
                </div>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-green-400 font-bold text-lg">✓</span>
                <div>
                  <strong className="text-purple-300">{t?.use4 || "Sensibilisation"}</strong>: {t?.use4Desc || "Comprendre le fonctionnement d'un test audiométrique et l'importance de la santé auditive."}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bibliographie */}
        <Card className="mb-6 bg-[#1a1f2e] border border-white/10 rounded-2xl shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <BookOpen className="h-6 w-6 text-white/70" />
              {t?.bibliography || "Bibliographie et références"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-2 text-sm">
              <div className="p-3 bg-white/5 rounded border border-white/10">
                <p className="font-medium text-white">ISO 389-8:2004</p>
                <p className="text-xs text-white/60 mt-1">
                  Acoustics — Reference zero for the calibration of audiometric equipment — Part 8: Reference equivalent threshold sound pressure levels for pure tones and circumaural earphones
                </p>
                <a 
                  href="https://www.iso.org/standard/38902.html" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:underline text-xs flex items-center gap-1 mt-1"
                >
                  <ExternalLink className="h-3 w-3" />
                  ISO.org
                </a>
              </div>

              <div className="p-3 bg-white/5 rounded border border-white/10">
                <p className="font-medium text-white">ISO 8253-1:2010</p>
                <p className="text-xs text-white/60 mt-1">
                  Acoustics — Audiometric test methods — Part 1: Pure-tone air and bone conduction audiometry
                </p>
                <a 
                  href="https://www.iso.org/standard/43601.html" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:underline text-xs flex items-center gap-1 mt-1"
                >
                  <ExternalLink className="h-3 w-3" />
                  ISO.org
                </a>
              </div>

              <div className="p-3 bg-white/5 rounded border border-white/10">
                <p className="font-medium text-white">ISO 226:2003</p>
                <p className="text-xs text-white/60 mt-1">
                  Acoustics — Normal equal-loudness-level contours
                </p>
                <a 
                  href="https://www.iso.org/standard/34222.html" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:underline text-xs flex items-center gap-1 mt-1"
                >
                  <ExternalLink className="h-3 w-3" />
                  ISO.org
                </a>
              </div>

              <div className="p-3 bg-white/5 rounded border border-white/10">
                <p className="font-medium text-white">World Health Organization (2021)</p>
                <p className="text-xs text-white/60 mt-1">
                  World report on hearing
                </p>
                <a 
                  href="https://www.who.int/publications/i/item/9789240020481" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:underline text-xs flex items-center gap-1 mt-1"
                >
                  <ExternalLink className="h-3 w-3" />
                  WHO.int
                </a>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact */}
        <Alert className="bg-blue-500/10 border-blue-500/30 mb-8">
          <AlertDescription className="text-white">
            <p className="font-semibold mb-2 text-blue-300">{t?.questions || "Questions sur notre méthodologie ?"}</p>
            <p className="text-sm text-white/80">
              {t?.contactDesc || "Nous sommes transparents sur notre approche et ses limitations. Pour toute question technique ou scientifique, n'hésitez pas à nous contacter."}
            </p>
          </AlertDescription>
        </Alert>
      </div>
    </div>
  );
};

export default Methodology;